/**
 * Program health, read from the sante_* charts of the dashboard (docs/superset/client-dashboards/sante.md).
 * Each program is measured against its objective (PROGRAM_TYPE), each measure is « n out of d » units read at a fixed age.
 * This module imports nothing (the colors flag is in program-health-levels.ts): the e2e specs test its rules directly.
 */

// The fields of a dashboard chart read here (DashboardChartType of features/admin/analytics/api/dashboard.ts).
type RowType = Record<string, unknown>
export type HealthChartType = { key?: string | null; title: string; error?: true; rows: RowType[] }

export type ProgramObjectiveType = 'MATCHING' | 'SESSIONS' | 'CURRICULUM' | 'ON_DEMAND' | 'UNDECLARED'
const OBJECTIVES: ProgramObjectiveType[] = ['MATCHING', 'SESSIONS', 'CURRICULUM', 'ON_DEMAND', 'UNDECLARED']

export const OBJECTIVE_LABELS: Record<ProgramObjectiveType, string> = {
  MATCHING: 'Mise en relation',
  SESSIONS: 'Parcours de sessions',
  CURRICULUM: 'Cursus noté',
  ON_DEMAND: 'À la demande',
  UNDECLARED: 'Objectif à déclarer',
}

/* ---------- Levels (off in v1) ---------- */

export type HealthLevelType = 'good' | 'watch' | 'act'
export type ThresholdsType = { good: number; bad: number }

// Provisional thresholds of sante.md (green / red share).
export const THRESHOLDS = {
  matching: { good: 0.55, bad: 0.45 },
  access: { good: 0.8, bad: 0.6 },
  start: { good: 0.85, bad: 0.7 },
  continuity: { good: 0.9, bad: 0.75 },
  onTrack: { good: 0.7, bad: 0.5 },
  curriculum: { good: 0.95, bad: 0.85 },
} satisfies Record<string, ThresholdsType>

// No level under 10 units, and a red needs at least 3 units in default.
export const MIN_UNITS = 10
const MIN_FAILURES = 3
// Out of the cycle (ended more than 60 days ago, not started, no period): the figures are a record, not an alert.
const NO_LEVEL_CYCLES = ['hors_cycle', 'a_lancer', 'sans_periode']
// While the program runs or is to come, a measure at 0 out of 0 is too early: no unit has reached the age of the reading
// yet. After its end, or without period, no unit will reach it any more.
const EARLY_CYCLES = ['en_cours', 'a_lancer']

export const LEVEL_STYLES: Record<HealthLevelType, { label: string; badge: string; fill: string }> = {
  good: { label: 'Bon niveau', badge: 'bg-success text-success-foreground', fill: 'bg-success' },
  watch: { label: 'À surveiller', badge: 'bg-warning text-warning-foreground', fill: 'bg-warning' },
  act: { label: 'À traiter', badge: 'bg-destructive text-destructive-foreground', fill: 'bg-destructive' },
}

/**
 * Share corrected by the Wilson interval at 80 %, with the constants of sante-reference.sql: green when the share
 * reaches the green threshold and the interval excludes red; red when it is under the red threshold, the interval
 * excludes green and at least 3 units are in default; orange otherwise.
 */
export function healthLevel(n: number, d: number, thresholds: ThresholdsType): HealthLevelType | null {
  if (d < MIN_UNITS) return null
  const p = n / d
  const margin = 1.28 * Math.sqrt(Math.max(0, (p * (1 - p)) / d + 0.41 / d ** 2))
  const low = (p + 0.82 / d - margin) / (1 + 1.64 / d)
  const high = (p + 0.82 / d + margin) / (1 + 1.64 / d)
  if (p >= thresholds.good && low >= thresholds.bad) return 'good'
  if (p < thresholds.bad && high < thresholds.good && d - n >= MIN_FAILURES) return 'act'
  return 'watch'
}

/* ---------- Measures ---------- */

// d = 0: not measured, see EARLY_CYCLES.
export type HealthMeasureType = { n: number; d: number; level: HealthLevelType | null }

const toNumber = (value: unknown): number | null => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) return Number(value)
  return null
}

function measureOf(
  row: RowType | undefined,
  name: string,
  thresholds: ThresholdsType | null,
  levels: boolean,
  cycle: string | null = null,
): HealthMeasureType | null {
  const d = toNumber(row?.[`${name}_d`])
  if (d === null) return null
  const n = Math.min(d, Math.max(0, toNumber(row?.[`${name}_n`]) ?? 0))
  const level = levels && thresholds && !NO_LEVEL_CYCLES.includes(cycle ?? '') ? healthLevel(n, d, thresholds) : null
  return { n, d, level }
}

/**
 * No green without proof (pas_vert of sante-reference.sql): with at least 10 sessions due and fewer than half of them
 * attested (report or presence), groups « on track » are orange at best.
 */
function capUnproven(measure: HealthMeasureType | null, due: number | null, attested: number | null) {
  if (measure?.level === 'good' && due !== null && due >= 10 && (attested ?? 0) < 0.5 * due) measure.level = 'watch'
  return measure
}

export const TOO_EARLY = 'Trop tôt pour mesurer'
export const NOT_MEASURED = 'Non mesuré pour ce programme'
export const NOTHING_TO_WATCH = 'Aucun groupe à suivre'
// What a measure at 0 out of 0 says; tooShort: the program lasts less than the age of the reading.
const emptyOf = (cycle: string | null, tooShort = false) =>
  !tooShort && (cycle === null || EARLY_CYCLES.includes(cycle)) ? TOO_EARLY : NOT_MEASURED
// The exact duration of the program (duree_j, not rounded) against the age of a reading: shorter, it is never read.
const shorterThan = (duration: number | null, days: number) => duration !== null && duration < days

export const outOf = (m: HealthMeasureType) => `${m.n.toLocaleString('fr-FR')} sur ${m.d.toLocaleString('fr-FR')}`
export const shareOf = (m: HealthMeasureType) => (m.d > 0 ? Math.max(0, Math.min(100, (100 * m.n) / m.d)) : 0)

// Colors off: the primary color, as every bar of the page. Colors on but no level (under 10 units): grey.
export const barFill = (m: HealthMeasureType, levels: boolean) =>
  m.level ? LEVEL_STYLES[m.level].fill : levels ? 'bg-muted-foreground/50' : 'bg-primary'

const plural = (n: number, word: string) => `${n.toLocaleString('fr-FR')} ${word}${n > 1 ? 's' : ''}`

/**
 * The groups a measure leaves out, by reason (sante.md, panne_fini, panne_recent, panne_sans_session, route_attente,
 * demarre_attente): without it, « 13 sur 13 » reads as a wrong count of the 18 groups. null when none is left out, or
 * for a measure that does not count groups. scale: the validated number of sessions per group (bareme).
 */
export function excludedOf(row: RowType | undefined, name: string, scale: number | null): string | null {
  // Each reason, with its verb for one group and for several.
  const sessions = scale ? plural(scale, 'session') : null
  const reasons: Record<string, Array<[string, string, string]>> = {
    panne: [
      sessions
        ? ['panne_fini', `a fait ses ${sessions} prévues`, `ont fait leurs ${sessions} prévues`]
        : ['panne_fini', 'a fait toutes ses sessions prévues', 'ont fait toutes leurs sessions prévues'],
      ['panne_recent', 'a moins de 60 jours', 'ont moins de 60 jours'],
      ['panne_sans_session', "n'a encore eu aucune session", "n'ont encore eu aucune session"],
    ],
    route: [['route_attente', "n'a pas encore l'âge de la mesure", "n'ont pas encore l'âge de la mesure"]],
    demarre: [['demarre_attente', 'a moins de 45 jours', 'ont moins de 45 jours']],
  }
  const parts = (reasons[name] ?? []).flatMap(([column, one, many]) => {
    const n = toNumber(row?.[column]) ?? 0
    return n > 0 ? [`${plural(n, 'groupe')} ${n > 1 ? many : one}`] : []
  })
  return parts.length ? `Hors mesure : ${parts.join(', ')}.` : null
}

/* ---------- « À relancer » lists ---------- */

type ListType = {
  objectives: ProgramObjectiveType[]
  // What one row counts: a person, one of the two members of a group (counted by group), a session.
  unit: 'person' | 'pair' | 'session'
  // Shown next to the count, as in the mock-ups.
  gesture: string | null
  // Requesting mentees without a group: a manager clause (groupe_id) never keeps any of them.
  adminOnly?: true
}

// The lists relance_<raison>, in their order on the page (« Jamais connectés » last, as in the mock-ups), with the
// objectives they apply to (sante.md, « Objectifs » column).
const LISTS: Record<string, ListType> = {
  sans_reponse: {
    objectives: ['MATCHING'],
    unit: 'person',
    gesture: "leur binôme leur a écrit, ils n'ont pas répondu",
  },
  sans_echange: { objectives: ['MATCHING'], unit: 'pair', gesture: 'relancer les deux, puis proposer un autre mentor' },
  en_attente: {
    objectives: ['MATCHING'],
    unit: 'person',
    gesture: 'mentorés qui doivent valider un mentor',
    adminOnly: true,
  },
  aucun_mentor: {
    objectives: ['MATCHING'],
    unit: 'person',
    gesture: 'critères à élargir ou mentors à recruter',
    adminOnly: true,
  },
  en_panne: { objectives: ['SESSIONS', 'CURRICULUM'], unit: 'pair', gesture: 'proposer une date au mentor' },
  sans_note: { objectives: ['CURRICULUM'], unit: 'person', gesture: null },
  sans_compte_rendu: {
    objectives: ['SESSIONS', 'CURRICULUM', 'ON_DEMAND'],
    unit: 'session',
    gesture: 'rappeler le compte rendu au mentor',
  },
  sans_demarrage: { objectives: ['SESSIONS'], unit: 'pair', gesture: 'proposer une première date au mentor' },
  jamais_connecte: { objectives: OBJECTIVES, unit: 'person', gesture: 'inscrits depuis plus de 7 jours' },
}
const LIST_ORDER = Object.keys(LISTS)
const listOf = (key: string | null | undefined) =>
  key?.startsWith('relance_') ? (LISTS[key.slice('relance_'.length)] ?? null) : null

/** Whether a chart is shown for this objective: only the lists that do not apply to it are left out. */
export function listApplies(key: string | null | undefined, objective: ProgramObjectiveType | null) {
  const list = listOf(key)
  // A list the table does not know (the v1 lists) stays.
  return !objective || !list || list.objectives.includes(objective)
}

export const listForAdminsOnly = (key: string | null | undefined) => !!listOf(key)?.adminOnly

/**
 * The objective the lists are filtered on. Without one from the sante_* charts (no pair and no request yet), the lists
 * of an objective are empty by construction: only those of every objective stay. A sante_* chart in error tells
 * nothing: every list stays.
 */
export function listsObjective(charts: HealthChartType[], health: ProgramHealthType | null) {
  if (health) return health.objective
  const healthCharts = charts.filter(isHealthChart)
  return healthCharts.length > 0 && healthCharts.every((c) => !c.error) ? 'UNDECLARED' : null
}

/** The known lists in their page order, in the places they hold among the charts: the other charts do not move. */
export function sortLists<T extends { key?: string | null }>(charts: T[]): T[] {
  const rank = (chart: T) => (listOf(chart.key) ? LIST_ORDER.indexOf(chart.key!.slice('relance_'.length)) : -1)
  const places = charts.flatMap((chart, index) => (rank(chart) >= 0 ? [index] : []))
  const sorted = places.map((index) => charts[index]).sort((a, b) => rank(a) - rank(b))
  const result = [...charts]
  places.forEach((place, index) => (result[place] = sorted[index]))
  return result
}

/** The line under a list title: « 6 sessions », « 10 groupes », « 13 personnes », then the gesture when there is one. */
export function listSummary(chart: { key?: string | null; rows: RowType[] }) {
  const list = listOf(chart.key)
  const count =
    list?.unit === 'pair'
      ? // By id: two groups can share a name (first names) or have none (« — »). The name stands in for an older model.
        plural(new Set(chart.rows.map((row) => row.groupe_id ?? row.binome)).size, 'groupe')
      : plural(chart.rows.length, list?.unit === 'session' ? 'session' : 'personne')
  return list?.gesture && chart.rows.length > 0 ? `${count} · ${list.gesture}` : count
}

/** The title of a list; « À l'arrêt » carries the stall threshold of the program, as in the mock-ups. */
export function listTitle(chart: { key?: string | null; title: string }, health: ProgramHealthType | null) {
  if (chart.key === 'relance_en_panne' && health?.stopDays)
    return `Sans session depuis plus de ${plural(health.stopDays, 'jour')}`
  return chart.title
}

/* ---------- Program page ---------- */

export type HealthListType = { key: string; title: string }
export type HealthCauseType = { n: number; text: string; list: HealthListType | null }
// empty: what is shown instead of « 0 sur 0 »; excluded: the groups the measure leaves out, by reason.
export type HealthStepType = {
  label: string
  measure: HealthMeasureType
  text: string
  empty: string
  excluded: string | null
}

export type HealthCardType = {
  id: 'matching' | 'access' | 'rhythm' | 'curriculum' | 'on_demand' | 'undeclared'
  title: string
  // Shown by the info button next to the title.
  hint: string | null
  description: string | null
  // null: no figure (objective not declared).
  measure: HealthMeasureType | null
  empty: string
  unit: string
  // The units the measure leaves out, by reason (« Hors mesure : … »).
  excluded: string | null
  // What the unit counts, for « Moins de 10 groupes : à lire avec prudence ».
  noun: string
  explanation: string
  causes: HealthCauseType[]
  steps: HealthStepType[]
}

export type ProgramHealthType = {
  objective: ProgramObjectiveType
  levels: boolean
  // Stall threshold of the program (arret_j), in days.
  stopDays: number | null
  cards: HealthCardType[]
}

export const HEALTH_KEYS = ['sante_binomes', 'sante_mentores', 'sante_acces']
export const isHealthChart = (chart: { key?: string | null }) => !!chart.key && HEALTH_KEYS.includes(chart.key)
// The objectives whose cards read each health chart; an undeclared objective reads none.
const HEALTH_CHART_OBJECTIVES: Record<string, ProgramObjectiveType[]> = {
  sante_binomes: ['MATCHING', 'SESSIONS', 'CURRICULUM', 'ON_DEMAND'],
  sante_mentores: ['MATCHING', 'CURRICULUM'],
  sante_acces: ['MATCHING'],
}
/** Whether a health chart feeds a card of this objective: one in error that feeds none is not announced as missing. */
export const healthChartApplies = (key: string | null | undefined, objective: ProgramObjectiveType | null) =>
  !objective || !key || !HEALTH_CHART_OBJECTIVES[key] || HEALTH_CHART_OBJECTIVES[key].includes(objective)

export const objectiveOf = (value: unknown): ProgramObjectiveType | null => {
  if (typeof value !== 'string' || value === '') return null
  // An unknown value is read as not declared: no figure rather than a wrong one.
  return OBJECTIVES.find((o) => o === value) ?? 'UNDECLARED'
}

// 90 → « les 3 premiers mois », 30 → « le premier mois », 40 → « les 40 premiers jours ».
function firstDays(days: number) {
  const rounded = Math.max(1, Math.round(days))
  if (rounded % 30 === 0) return rounded === 30 ? 'le premier mois' : `les ${rounded / 30} premiers mois`
  return rounded === 1 ? 'le premier jour' : `les ${rounded} premiers jours`
}

/**
 * The health cards of a program, from all the charts of its dashboard (any tab). null when the dashboard has no
 * sante_* chart (v1 dashboards), or when nothing tells the objective (no pair and no request yet).
 */
export function buildProgramHealth(charts: HealthChartType[], levels: boolean): ProgramHealthType | null {
  const rowOf = (key: string) => {
    const chart = charts.find((c) => c.key === key && !c.error)
    return chart ? (chart.rows[0] ?? {}) : undefined
  }
  const pairs = rowOf('sante_binomes')
  const mentees = rowOf('sante_mentores')
  const access = rowOf('sante_acces')
  // The aggregates of a dataset without any row are all null: the objective comes from the first that has rows.
  const objective = objectiveOf(pairs?.objectif) ?? objectiveOf(mentees?.objectif) ?? objectiveOf(access?.objectif)
  if (!objective) return null
  const cycle = typeof pairs?.cycle === 'string' ? pairs.cycle : null
  const horizon = toNumber(pairs?.horizon_j)
  const duration = toNumber(pairs?.duree_j)
  const stopDays = toNumber(pairs?.arret_j)
  const health: ProgramHealthType = { objective, levels, stopDays, cards: [] }

  const list = (raison: string): HealthListType | null => {
    const chart = charts.find((c) => c.key === `relance_${raison}` && !c.error)
    // An empty list is not offered. The causes count everyone; the lists leave out the programs ended more than 60 days
    // ago (hors_cycle) and show a person once per program, under their first reason (never signed in comes first).
    return chart && chart.rows.length > 0 && listApplies(chart.key, objective)
      ? { key: chart.key!, title: listTitle(chart, health) }
      : null
  }
  const cause = (n: number | null, text: string, raison: string | null = null): HealthCauseType[] =>
    n && n > 0 ? [{ n, text, list: raison ? list(raison) : null }] : []

  const cards = health.cards
  // No pair (manager without groups, program not started): nothing measured on pairs, only the requests.
  const hasPairs = pairs === undefined || (toNumber(pairs.binomes) ?? 0) > 0

  if (hasPairs && objective === 'MATCHING') {
    cards.push({
      id: 'matching',
      title: 'Mise en relation',
      hint: "Mentorés dont le groupe a au moins 30 jours. Un mentoré est en contact quand, dans les 30 jours qui suivent la formation de son groupe, son mentor et lui se sont écrit dans les deux sens, ou qu'une session a eu lieu. Pendant le programme, seuls les groupes de moins de 4 mois comptent ; dans les 2 mois qui suivent la fin, tous les groupes.",
      description: 'Le programme a pour objectif que chaque mentoré trouve un mentor et échange avec lui.',
      measure: measureOf(mentees, 'mer', THRESHOLDS.matching, levels, cycle),
      empty: emptyOf(cycle, shorterThan(duration, 30)),
      unit: 'mentorés en contact',
      excluded: null,
      noun: 'mentorés',
      explanation:
        'Dans le mois qui suit la mise en relation, le mentor et le mentoré ont tous deux écrit, ou une session a eu lieu.',
      causes: [],
      steps: [],
    })
  }

  // Requests (admins only): left out when the pairs are imposed, or when nobody has looked for a mentor.
  if (objective === 'MATCHING' && access && objectiveOf(access.objectif) && !toNumber(access.imposes)) {
    const causes = [
      ...cause(toNumber(access.en_attente), "n'ont pas encore choisi parmi les mentors proposés", 'en_attente'),
      ...cause(
        toNumber(access.aucun_compatible),
        "n'ont trouvé aucun mentor compatible avec leurs critères",
        'aucun_mentor',
      ),
      ...cause(toNumber(access.aucun_disponible), "n'ont trouvé aucun mentor disponible", 'aucun_mentor'),
      ...cause(toNumber(access.servi_tard), 'ont eu un mentor, mais après 30 jours'),
      ...cause(toNumber(access.autre), 'sont sans mentor pour une autre raison'),
    ]
    cards.push({
      id: 'access',
      title: 'Demandes servies en 30 jours',
      hint: 'Mentorés qui ont cherché un mentor sur la plateforme il y a au moins 30 jours. Une demande est servie quand le mentoré est dans un groupe au plus tard 30 jours après sa première recherche. Pendant le programme, seules les recherches de moins de 4 mois comptent ; dans les 2 mois qui suivent la fin, toutes.',
      description: 'Mentorés qui ont un mentor 30 jours après leur première recherche.',
      measure: measureOf(access, 'acces', THRESHOLDS.access, levels, cycle),
      empty: emptyOf(cycle),
      unit: 'demandes servies',
      excluded: null,
      noun: 'demandes',
      explanation: '',
      causes,
      steps: [],
    })
  }

  if (hasPairs && objective === 'SESSIONS') {
    const scale = toNumber(pairs?.bareme)
    const h = horizon ?? 90
    const stop = stopDays ?? 60
    const reports = toNumber(pairs?.sessions_echues)
    const attested = toNumber(pairs?.sessions_attestees)
    const start = measureOf(pairs, 'demarre', THRESHOLDS.start, levels, cycle)
    // NULL without a validated scale; measured only while the program runs (sante.md, panne_d).
    const continuity =
      cycle === null || EARLY_CYCLES.includes(cycle)
        ? measureOf(pairs, 'panne', THRESHOLDS.continuity, levels, cycle)
        : null
    const steps: HealthStepType[] = []
    if (start)
      steps.push({
        label: 'Démarrage',
        measure: start,
        text: 'groupes avec une première session dans les 45 jours',
        empty: emptyOf(cycle, shorterThan(duration, 45)),
        excluded: excludedOf(pairs, 'demarre', scale),
      })
    if (continuity)
      steps.push({
        label: 'Continuité',
        measure: continuity,
        text: `groupes sans arrêt de plus de ${plural(stop, 'jour')}`,
        // No group read: none is 60 days old yet (too early), or every group of 60 days or more has finished its scale
        // or never started (the start step counts those).
        empty: shorterThan(duration, 60)
          ? NOT_MEASURED
          : (toNumber(pairs?.binomes60) ?? 0) > 0
            ? NOTHING_TO_WATCH
            : emptyOf(cycle),
        excluded: excludedOf(pairs, 'panne', scale),
      })
    if (reports !== null)
      steps.push({
        label: 'Comptes rendus',
        measure: { n: Math.min(reports, attested ?? 0), d: reports, level: null },
        text: 'sessions passées avec un compte rendu ou une présence',
        empty: emptyOf(cycle),
        excluded: null,
      })
    cards.push({
      id: 'rhythm',
      title: 'Rythme des sessions',
      hint: `Groupes formés depuis au moins ${plural(h, 'jour')}. Un groupe est au rythme prévu quand il a tenu au moins 2 sessions dans ses ${Math.round(h)} premiers jours, dont une après le ${Math.round(h / 3)}e jour. Une session compte quand sa date est passée et qu'elle n'a pas été annulée.`,
      description: scale
        ? `Le programme prévoit ${plural(scale, 'session')} par groupe, hors objectif ouvert.`
        : `Aucun nombre de sessions par groupe n'est validé pour ce programme : le rythme se lit sur ${firstDays(h)} de chaque groupe.`,
      measure: capUnproven(measureOf(pairs, 'route', THRESHOLDS.onTrack, levels, cycle), reports, attested),
      empty: emptyOf(cycle),
      unit: 'groupes au rythme prévu',
      excluded: excludedOf(pairs, 'route', scale),
      noun: 'groupes',
      explanation: `Au moins 2 sessions dans ${firstDays(h)} du groupe, dont une après ${firstDays(h / 3)}${
        scale ? " : le rythme qu'il faut pour finir le parcours à temps." : '.'
      }`,
      causes: [],
      steps,
    })
  }

  if (hasPairs && objective === 'CURRICULUM') {
    const measure = measureOf(mentees, 'cadre', THRESHOLDS.curriculum, levels, cycle)
    const absent = toNumber(mentees?.absents)
    cards.push({
      id: 'curriculum',
      title: 'Cursus noté',
      hint: 'Mentorés qui ont au moins une session passée depuis plus de 30 jours. Un mentoré compte quand chacune de ces sessions porte une note au-dessus de 0.',
      description: 'Le programme impose des sessions notées : chaque mentoré doit être noté à chaque session.',
      measure,
      empty: emptyOf(cycle),
      unit: 'mentorés notés à chaque session',
      excluded: null,
      noun: 'mentorés',
      explanation: 'Une note au-dessus de 0 sur chacune des sessions passées depuis plus de 30 jours.',
      causes: measure ? cause(measure.d - measure.n, "n'ont pas de note sur au moins une session", 'sans_note') : [],
      steps:
        measure && absent !== null
          ? [
              {
                label: 'Absences',
                measure: { n: Math.min(absent, measure.d), d: measure.d, level: null },
                text: 'mentorés absents au moins une fois',
                empty: emptyOf(cycle),
                excluded: null,
              },
            ]
          : [],
    })
  }

  if (hasPairs && objective === 'ON_DEMAND') {
    cards.push({
      id: 'on_demand',
      title: 'À la demande',
      hint: 'Groupes formés depuis au moins 90 jours. Un groupe compte quand sa première session a eu lieu au plus tard 90 jours après sa formation.',
      description: 'Le programme propose une consultation ponctuelle : chaque groupe doit avoir au moins une session.',
      // No threshold: never colored.
      measure: measureOf(pairs, 'servi90', null, levels, cycle),
      empty: emptyOf(cycle, shorterThan(duration, 90)),
      unit: 'groupes avec une première session dans les 90 jours',
      excluded: null,
      noun: 'groupes',
      explanation: '',
      causes: [],
      steps: [],
    })
  }

  if (hasPairs && objective === 'UNDECLARED') {
    cards.push({
      id: 'undeclared',
      title: 'Objectif du programme',
      hint: null,
      description: null,
      measure: null,
      empty: '',
      unit: '',
      excluded: null,
      noun: '',
      explanation:
        "L'objectif de ce programme n'est pas encore déclaré. Ses chiffres de santé s'afficheront ici dès que l'équipe Constel l'aura renseigné.",
      causes: [],
      steps: [],
    })
  }

  // A card whose chart is unavailable is left out: the chart in error says so on its own.
  health.cards = cards.filter((card) => card.id === 'undeclared' || card.measure)
  return health
}

/* ---------- « Tous les programmes » ---------- */

export type OverviewObjectiveType = {
  // The objective, written small above the figure; null when it is not known.
  type: string | null
  // « 102 sur 112 », or the reason there is no figure.
  value: string
  unit: string
  // null: no bar.
  measure: HealthMeasureType | null
  // The groups the measure leaves out, by reason (« Hors mesure : … »).
  excluded: string | null
}

// The measure that stands for each objective in the overview; for sessions, the first one already readable.
const OVERVIEW_MEASURES: Record<
  Exclude<ProgramObjectiveType, 'UNDECLARED'>,
  Array<{ name: string; unit: string; noun: string; thresholds: ThresholdsType | null }>
> = {
  MATCHING: [
    { name: 'mer', unit: 'mentorés en contact dans le mois', noun: 'mentorés', thresholds: THRESHOLDS.matching },
  ],
  SESSIONS: [
    { name: 'route', unit: 'groupes au rythme prévu', noun: 'groupes', thresholds: THRESHOLDS.onTrack },
    { name: 'panne', unit: 'groupes démarrés sans arrêt prolongé', noun: 'groupes', thresholds: THRESHOLDS.continuity },
    { name: 'demarre', unit: 'groupes démarrés dans les 45 jours', noun: 'groupes', thresholds: THRESHOLDS.start },
  ],
  CURRICULUM: [
    { name: 'cadre', unit: 'mentorés notés à chaque session', noun: 'mentorés', thresholds: THRESHOLDS.curriculum },
  ],
  ON_DEMAND: [
    { name: 'servi90', unit: 'groupes avec une première session dans les 90 jours', noun: 'groupes', thresholds: null },
  ],
}

// The age of the reading of each objective's measure, when a program can be shorter than it (as on the program page:
// mise en relation at 30 days, à la demande at 90 days).
const READ_AGE: Partial<Record<ProgramObjectiveType, number>> = { MATCHING: 30, ON_DEMAND: 90 }

/**
 * The cycle of a row of the overview: its `cycle` column when the chart has one, else read from its status and end date
 * (JJ/MM/AAAA). A program still active after its end (prolonge) then reads as out of cycle 60 days after it: no color
 * rather than a wrong one.
 */
function overviewCycle(row: RowType, now: number): string | null {
  if (typeof row.cycle === 'string') return row.cycle
  if (!('fin' in row) || !('statut' in row)) return null
  if (!row.debut || !row.fin) return 'sans_periode'
  if (row.statut === 'À venir') return 'a_lancer'
  if (row.statut !== 'Terminé') return 'en_cours'
  const [day, month, year] = String(row.fin).split('/').map(Number)
  const end = Date.UTC(year, month - 1, day)
  return Number.isFinite(end) && now - end <= 60 * 864e5 ? 'lecture_finale' : 'hors_cycle'
}

/** The « Objectif du programme » cell of a row of the overview (chart `programmes` with the `objectif` column). */
export function overviewObjective(row: RowType, levels: boolean, now = Date.now()): OverviewObjectiveType {
  const objective = objectiveOf(row.objectif)
  const type = objective && objective !== 'UNDECLARED' ? OBJECTIVE_LABELS[objective] : null
  if (objective === 'UNDECLARED' || (!objective && (toNumber(row.binomes) ?? 0) > 0))
    return {
      type: OBJECTIVE_LABELS.UNDECLARED,
      value: '—',
      unit: "à renseigner par l'équipe Constel",
      measure: null,
      excluded: null,
    }
  if (!objective || !(toNumber(row.binomes) ?? 0))
    return {
      type,
      value: 'Pas encore de groupe',
      unit: "aucun groupe formé pour l'instant",
      measure: null,
      excluded: null,
    }
  const cycle = overviewCycle(row, now)
  for (const candidate of OVERVIEW_MEASURES[objective]) {
    const measure = measureOf(row, candidate.name, candidate.thresholds, levels, cycle)
    if (!measure || measure.d === 0) continue
    // The sessions due and attested are read when the chart has them.
    if (candidate.name === 'route')
      capUnproven(measure, toNumber(row.sessions_echues), toNumber(row.sessions_attestees))
    const caution = measure.d < MIN_UNITS ? ` · moins de 10 ${candidate.noun}, à lire avec prudence` : ''
    const excluded = excludedOf(row, candidate.name, toNumber(row.bareme))
    return { type, value: outOf(measure), unit: candidate.unit + caution, measure, excluded }
  }
  const empty = emptyOf(cycle, shorterThan(toNumber(row.duree_j), READ_AGE[objective] ?? 0))
  return {
    type,
    value: empty,
    unit:
      empty === TOO_EARLY
        ? "aucun groupe n'a encore l'âge de la mesure"
        : "aucun groupe n'a atteint l'âge de la mesure pendant le programme",
    measure: null,
    excluded: null,
  }
}

export const OVERVIEW_LEGEND =
  'Chaque programme est mesuré selon son objectif. Mise en relation : mentorés en contact dans le mois qui suit la mise en relation. Parcours de sessions : groupes au rythme prévu, ou à défaut sans arrêt prolongé. Cursus noté : mentorés notés à chaque session. À la demande : groupes avec une première session dans les 90 jours.'
